(() => {
  if (window.ffnMotion || !('IntersectionObserver' in window) || !Element.prototype.animate) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const compact = matchMedia('(max-width: 760px)');
  const seen = new WeakSet();
  const activeAnimations = new Set();
  const depth = new Set();
  const visibleDepth = new Set();
  const headings = '.ffn-section-top,.ffn-route-heading,.ffn-about-editorial__head,.ffn-goal-path__head,.ffn-faq-page__intro';
  const cards = '.ffn-path-card,.ffn-journey-goals>a,.ffn-world,.ffn-native-grid>div,[data-discovery-card],#product-grid>li,.ffn-goal,.ffn-goal-page,.ffn-world-category,.ffn-world-features>div,.ffn-world-pack,.ffn-bundle,.ffn-brand-value,.ffn-about-chapter,.ffn-goal-path__steps>li,.ffn-pdp-detail,.ffn-sportline-proof__grid>article,.ffn-world-benefits__grid>article';
  const copy = '.ffn-world-offer__copy,.ffn-world-editorial>div,.ffn-sport-editorial__copy,.ffn-story-copy,.ffn-routine-copy,.ffn-pack__copy,.ffn-faq-layout>div:first-child,.ffn-newsletter .ffn-wrap>div:first-child,.ffn-page-signup,.ffn-pdp-details__intro,.ffn-footer-contact';
  const photos = '.ffn-world-offer__photo>img,.ffn-pack-campaign__image,.ffn-world-pack__stage>img,.ffn-sport-editorial__photo>img,.ffn-routine-photo>img,.ffn-world-image,.ffn-routine-backdrop,.ffn-routine-stage-word';
  const layers = '.ffn-hero-slide>.ffn-hero-image,.ffn-hero-slide>.ffn-hero-people,.ffn-hero-slide>.ffn-hero-athletes,.ffn-route-hero>img,.ffn-contact-hero__image,.ffn-collection-intro__image';
  const editorial = '.ffn-motion-media>img,.ffn-story-slide:not(:first-child)>img';
  const select = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  function play(el, delay = 0) {
    if (reduce.matches || el.closest('[inert],[hidden]') || !el.getClientRects().length) return;
    const calm = el.closest('.ffn-route--vital,.ffn-route--glow,.ffn-world-pack-section--vital,.ffn-world-pack-section--glow');
    const media = el.matches('.ffn-motion-media,.ffn-story-visual,.ffn-sport-editorial__photo');
    const animation = el.animate(media ? [
      { opacity: .35, clipPath: 'inset(7% 0 0 0)' },
      { opacity: 1, clipPath: 'inset(0% 0 0 0)' }
    ] : [
      { opacity: .12, translate: `0 ${compact.matches ? 30 : calm ? 36 : 48}px` },
      { opacity: 1, translate: '0 0' }
    ], { duration: calm ? 800 : 720, delay, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'backwards' });
    activeAnimations.add(animation);
    animation.finished.catch(() => {}).finally(() => activeAnimations.delete(animation));
    el.dataset.ffnEntered = 'true';
  }
  const enter = new IntersectionObserver(entries => {
    let step = 0;
    for (const { target, isIntersecting } of entries) {
      if (!isIntersecting || target.closest('[inert],[hidden]')) continue;
      enter.unobserve(target);
      play(target, Math.min(step++ % 4, 3) * 90);
    }
  }, { threshold: .06, rootMargin: '0px 0px -24px 0px' });
  let frame = 0;
  function renderDepth() {
    frame = 0;
    if (reduce.matches || document.hidden) return;
    const updates = [];
    const headerBottom = Math.max(0, document.querySelector('.ffn-nav')?.getBoundingClientRect().bottom || 0);
    for (const img of visibleDepth) {
      if (!img.isConnected) { visibleDepth.delete(img); depth.delete(img); continue; }
      if (img.closest('[hidden],[inert]')) continue;
      const hero = img.closest('.ffn-hero-slide,.ffn-route-hero,.ffn-contact-hero,.ffn-collection-intro');
      const scene = img.classList.contains('ffn-routine-backdrop');
      const word = img.classList.contains('ffn-routine-stage-word');
      const product = img.matches('.ffn-routine-photo>img');
      const stage = img.closest('.ffn-routine-stage');
      const rect = (hero || stage || img.parentElement).getBoundingClientRect();
      const progress = Math.max(-1, Math.min(1, (innerHeight / 2 - rect.top - rect.height / 2) / innerHeight));
      const background = img.matches('.ffn-hero-image,.ffn-route-hero>img:first-child,.ffn-contact-hero__image,.ffn-collection-intro__image');
      let y;
      if (hero) {
        // Keep the approved composition at rest. On exit, the room lags behind
        // the people and both lag behind the copy: actual scroll-linked depth.
        // Positive travel cannot expose the lower image edge during hero exit.
        const exit = Math.max(0, headerBottom - rect.top);
        const speed = background ? (compact.matches ? .42 : .58) : (compact.matches ? .20 : .28);
        y = Math.min(exit, rect.height) * speed;
      } else if (stage || scene) {
        // All shopping layers share a stable scene reference; they cross in
        // opposite directions while the price and purchase controls stay put.
        const distance = innerHeight / 2 - rect.top - rect.height / 2;
        const limit = scene ? Math.min(150, rect.height * .11)
          : word ? (compact.matches ? 58 : 110) : (compact.matches ? 60 : 110);
        const speed = scene ? .34 : word ? .24 : -.30;
        y = Math.max(-limit, Math.min(limit, distance * speed));
      } else {
        y = progress * Math.min(compact.matches ? 48 : 90, rect.height * .10);
      }
      updates.push([img, y]);
    }
    updates.forEach(([img, y]) => img.style.setProperty('--ffn-depth-y', `${y.toFixed(2)}px`));
  }
  function schedule() { if (!frame && visibleDepth.size && !reduce.matches) frame = requestAnimationFrame(renderDepth); }
  const depthObserver = new IntersectionObserver(entries => {
    entries.forEach(({target, isIntersecting}) => isIntersecting ? visibleDepth.add(target) : visibleDepth.delete(target));
    schedule();
  });
  function observe(el) {
    if (seen.has(el) || el.closest('quick-add-modal,cart-drawer,.ffn-hero-slide,dialog')) return;
    seen.add(el);
    el.dataset.ffnMotion = 'entry';
    // No CSS hidden state: content remains usable if JS or observers stop.
    el.classList.remove('ffn-reveal', 'is-pending', 'scroll-trigger', 'animate--slide-in', 'animate--fade-in');
    if (!reduce.matches) enter.observe(el);
  }
  function scan(root = document) {
    select(root, headings).forEach(group => select(group, '.ffn-eyebrow,h2,p').forEach(observe));
    select(root, cards).forEach(observe);
    select(root, '.ffn-motion-media,.ffn-story-visual,.ffn-sport-editorial__photo,.ffn-faq details,.ffn-contact-shell .contact-information>div,.ffn-policy-page__toc,.ffn-faq-page__contact-inner>div').forEach(observe);
    select(root, copy).forEach(group => {
      if (group.closest(cards)) return;
      [...group.children].filter(el => el.matches('h2,h3,p,.ffn-eyebrow,.ffn-routine-price,.ffn-actions,a')).forEach(observe);
    });
    select(root, '.ffn-faq-page__hero-inner>*,.ffn-contact-hero__content>*,.ffn-route-hero>.ffn-wrap>*,.ffn-collection-intro__content>*,.ffn-policy-page__hero-inner>*').forEach(observe);
    select(root, '.product__info-container>.product__title,.ffn-editorial .rte h2,.shopify-policy__title,.shopify-policy__body h2,.ffn-footer-grid>nav').forEach(observe);
    select(root, `${photos},${layers},${editorial}`).forEach(img => {
      if (depth.has(img)) return;
      depth.add(img); img.dataset.ffnDepth = img.matches('.ffn-route-athletes,.ffn-route-world-people') ? 'person'
        : img.matches('.ffn-route-hero>img:first-child,.ffn-contact-hero__image,.ffn-collection-intro__image') ? 'scene'
        : img.closest('.ffn-hero-slide') ? 'layer'
        : img.matches('.ffn-routine-backdrop') ? 'backdrop'
        : img.matches('.ffn-routine-stage-word,.ffn-routine-photo>img') ? 'foreground' : 'photo';
      depthObserver.observe(img);
    });
  }
  function init() {
    const main = document.getElementById('MainContent');
    if (!main) return;
    scan(main);
    const footer = document.querySelector('.ffn-footer');
    if (footer) scan(footer);
    new MutationObserver(records => {
      let revealed = 0;
      records.forEach(record => {
        if (record.type === 'childList') record.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
        else if (!record.target.hasAttribute('hidden') && record.target.getAttribute('aria-hidden') !== 'true') {
          scan(record.target); schedule();
          // Existing filtered cards also deserve feedback when shown again.
          if (record.attributeName === 'hidden' && record.oldValue !== null && record.target.matches(cards)) {
            const rect = record.target.getBoundingClientRect();
            if (rect.bottom > 0 && rect.top < innerHeight && record.target.dataset.ffnEntered) play(record.target, Math.min(revealed++, 3) * 70);
          }
        }
      });
    }).observe(main, { subtree: true, childList: true, attributes: true, attributeOldValue: true, attributeFilter: ['hidden','aria-hidden'] });
    document.addEventListener('shopify:section:load', event => scan(event.target));
    document.addEventListener('focusin', event => {
      activeAnimations.forEach(animation => { if (animation.effect?.target?.contains(event.target)) animation.finish(); });
    });
  }
  window.ffnMotion = { scan, version: '3.0' };
  addEventListener('scroll', schedule, {passive:true});
  addEventListener('resize', schedule, {passive:true});
  document.addEventListener('visibilitychange', schedule);
  reduce.addEventListener('change', () => {
    if (reduce.matches) {
      activeAnimations.forEach(animation => animation.cancel());
      enter.disconnect();
      depth.forEach(img => img.style.removeProperty('--ffn-depth-y'));
    } else {
      document.querySelectorAll('[data-ffn-motion]:not([data-ffn-entered])').forEach(el => enter.observe(el));
      schedule();
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
